import { HERO_LAYOUTS } from "@/lib/heroImage";
import { useElementHeight } from "@/hooks/useElementHeight";
import { useViewportSize } from "@/hooks/useViewportSize";
import {
  motion,
  transform,
  useMotionValue,
  useMotionValueEvent,
  useScroll,
  useTransform,
} from "motion/react";
import { useEffect, useRef } from "react";
import { BuildingLineArt } from "./BuildingLineArt";
import { heroHeader } from "./heroHeader";
import { heroPhoto } from "./heroPhoto";
import { HeroStatement } from "./HeroStatement";
import { IntegratedHeading, IntegratedParagraph } from "./IntegratedCopy";
import { LINE_ART } from "./lineArt";

// The statement fades out over this much scroll.
const STATEMENT_FADE_REM = 12.5;

// Fractions of the pinned scroll, which starts once the photo's bottom edge
// reaches the bottom of the viewport.
const PHASES = {
  statementOut: [0, 0.12],
  draw: [0.12, 0.45],
  photoOut: [0.35, 0.48],
  controlsIn: [0.45, 0.5],
  artSettle: [0.6, 1],
  copyIn: [0.6, 1],
};
const PIN_TRAVEL_VH = 1.2;

// Line art placement from the Figma hero frame (1440x1079), as fractions of the photo
const VILLA = {
  left: 215 / 1440,
  top: 720 / 1079,
  bottom: 976 / 1079,
  width: 1009 / 1440,
};
const PHOTO_ASPECT = 1440 / 1079;
const STROKE_OVER_PHOTO = "#e3e3de";
const STROKE_ON_WHITE = "#999da0";
const ART_ASPECT = LINE_ART.width / LINE_ART.height;

const CONTAINED_INSET_REM = 1;
const CONTAINED_MIN_HEIGHT_REM = 46.875;
const VILLA_BASE_CLEARANCE_REM = 3.75;
const CARD_RADIUS_REM = 1.5;
// The intro slides up over the end of the pin and stops this far below the
// paragraph; its own top margin adds the rest of the gap to the divider.
const INTRO_GAP_REM = 5;
// The fixed nav plus breathing room; the story centers in the space below it.
const HEADER_CLEARANCE_REM = 7;
// Scrolling back up, the photo rides this far above the story's heading, and
// the story fades over this share of the way as the photo lands.
const REVERSE_GAP_REM = 7.5;
const REVERSE_STORY_FADE = 0.3;

export function HeroScrollStory() {
  const ref = useRef<HTMLDivElement>(null);
  const { width, height, rem } = useViewportSize();
  const [headingRef, headingHeight] = useElementHeight<HTMLDivElement>();
  const [paragraphRef, paragraphHeight] = useElementHeight<HTMLDivElement>();
  const photo = photoFrame(width, height, rem);
  const art = villaArt(photo);
  const storyTop = art.top - headingHeight;
  const storyHeight = headingHeight + art.height + paragraphHeight;
  const settledTop =
    HEADER_CLEARANCE_REM * rem +
    Math.max((height - HEADER_CLEARANCE_REM * rem - storyHeight) / 2, 0);
  // The card scrolls with the page until the story sits centered below the
  // nav, so it holds still through the pin; the stage covers the whole
  // viewport while pinned.
  const pinStart = Math.max(storyTop - settledTop, 0);
  const settledShift = pinStart + settledTop - storyTop;
  const stageHeight = Math.max(
    photo.top + photo.height + photo.top,
    pinStart + height,
  );
  const pinTravel = height * PIN_TRAVEL_VH;
  // Negative when the story runs past the viewport, pushing the intro down.
  const introOverlap =
    stageHeight - pinStart - settledTop - storyHeight - INTRO_GAP_REM * rem;
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: [
      `${pinStart + height}px end`,
      `${pinStart + height + pinTravel}px end`,
    ],
  });
  const { scrollYProgress: approach } = useScroll({
    target: ref,
    offset: ["start start", `${pinStart + height}px end`],
  });

  const photoFade = useTransform(scrollYProgress, PHASES.photoOut, [1, 0]);
  const strokeColor = useTransform(
    photoFade,
    [1, 0],
    [STROKE_OVER_PHOTO, STROKE_ON_WHITE],
  );
  // Scrolling back up after the photo has faded freezes the story where it was
  // and slides the photo down over it, like un-pinned content. The slide only
  // starts once the photo is invisible and hands back to the forward animation
  // at the pin's start or once scrolling passes the reversal point again, so
  // both directions agree wherever they switch.
  const reversing = useMotionValue(0);
  const reversalPoint = useMotionValue(1);
  useMotionValueEvent(scrollYProgress, "change", (progress) => {
    const previous = scrollYProgress.getPrevious() ?? progress;
    if (reversing.get()) {
      if (progress >= reversalPoint.get() || progress <= 0) {
        reversing.set(0);
      }
    } else if (progress < previous && progress >= PHASES.photoOut[1]) {
      reversalPoint.set(progress);
      reversing.set(1);
    }
  });
  const storyProgress = useTransform(
    [scrollYProgress, reversing, reversalPoint],
    ([progress, isReversing, point]: number[]) =>
      isReversing ? point : progress,
  );
  const draw = useTransform(storyProgress, PHASES.draw, [0, 1]);
  const photoOpacity = useTransform(
    [photoFade, reversing],
    ([fade, isReversing]: number[]) => (isReversing ? 1 : fade),
  );
  // How far the story drifts down while scrolling back up from `point`: the
  // photo stays attached above its heading, entering from above the viewport,
  // and lands in place as the story reaches the card's bottom edge.
  const reverseDrift = (point: number) => {
    const headingTop =
      storyTop -
      pinStart +
      transform(point, PHASES.artSettle, [0, settledShift]);
    const cardBottom = photo.top + photo.height - pinStart;
    return (
      cardBottom - headingTop + Math.max(REVERSE_GAP_REM * rem, headingTop)
    );
  };
  const storyOpacity = useTransform(
    [scrollYProgress, reversing, reversalPoint],
    ([progress, isReversing, point]: number[]) =>
      isReversing
        ? transform(progress, [0, point * REVERSE_STORY_FADE], [0, 1])
        : 1,
  );
  const photoY = useTransform(
    [scrollYProgress, reversing, reversalPoint],
    ([progress, isReversing, point]: number[]) =>
      isReversing ? (-reverseDrift(point) * progress) / point : 0,
  );
  const photoZ = useTransform(reversing, (isReversing) =>
    isReversing ? 20 : 0,
  );
  // The card's top edge stays at its inset while the photo scrolls up under it.
  const photoClip = useTransform(
    [approach, photoY],
    ([approached, offset]: number[]) =>
      `inset(${approached * pinStart - offset}px 0 0 0 round ${CARD_RADIUS_REM * rem}px)`,
  );
  // The statement scrolls with the photo and has faded by the time the pin
  // starts; with no scroll before the pin it fades as the pin starts instead.
  const statementFadePx = Math.min(STATEMENT_FADE_REM * rem, pinStart);
  const statementOpacity = useTransform(
    [approach, scrollYProgress],
    ([approached, pinned]: number[]) =>
      statementFadePx > 0
        ? transform(approached * pinStart, [0, statementFadePx], [1, 0])
        : transform(pinned, PHASES.statementOut, [1, 0]),
  );
  const statementPointerEvents = useTransform(statementOpacity, (opacity) =>
    opacity > 0.5 ? "auto" : "none",
  );
  const controlsOpacity = useTransform(
    storyProgress,
    PHASES.controlsIn,
    [0, 1],
  );
  const copyOpacity = useTransform(storyProgress, PHASES.copyIn, [0, 1]);

  useMotionValueEvent(photoOpacity, "change", (value) =>
    heroHeader.photoOpacity.set(value),
  );
  useEffect(() => {
    heroHeader.photoOpacity.set(photoOpacity.get());
    return () => heroHeader.photoOpacity.set(0);
  }, [photoOpacity]);

  // The block is placed at the art's top with a transform rather than `top`,
  // because the heading height is measured after first paint and a transform
  // change doesn't count as a layout shift.
  const storyY = useTransform(
    [scrollYProgress, reversing, reversalPoint],
    ([progress, isReversing, point]: number[]) =>
      -headingHeight +
      transform(isReversing ? point : progress, PHASES.artSettle, [
        0,
        settledShift,
      ]) +
      (isReversing ? (reverseDrift(point) * (point - progress)) / point : 0),
  );

  return (
    <section
      ref={ref}
      id="top"
      className="relative"
      style={{ height: stageHeight + pinTravel, marginBottom: -introOverlap }}
    >
      <div
        className="sticky overflow-hidden"
        style={{ top: -pinStart, height: stageHeight }}
      >
        <motion.img
          src={heroPhoto.src}
          srcSet={heroPhoto.srcSet}
          sizes={HERO_LAYOUTS.scrollStory.sizes}
          alt="Park Hyatt guest villa at dusk with warm lighting over the pool terrace"
          className="absolute max-w-none rounded-3xl"
          style={{
            left: photo.left,
            top: photo.top,
            width: photo.width,
            height: photo.height,
            objectFit: "cover",
            objectPosition: `${photo.imageLeft - photo.left}px ${photo.imageTop - photo.top}px`,
            opacity: photoOpacity,
            y: photoY,
            zIndex: photoZ,
            clipPath: photoClip,
          }}
        />
        <motion.div
          aria-hidden
          className="pointer-events-none absolute rounded-3xl bg-gradient-to-b from-black/50 to-transparent to-70%"
          style={{
            left: photo.left,
            top: photo.top,
            width: photo.width,
            height: photo.height,
            opacity: photoOpacity,
            y: photoY,
            zIndex: photoZ,
            clipPath: photoClip,
          }}
        />

        <motion.div
          className="absolute inset-x-0 top-0 z-30 text-inverse"
          style={{
            opacity: statementOpacity,
            y: photoY,
            pointerEvents: statementPointerEvents,
          }}
        >
          <HeroStatement />
        </motion.div>

        <motion.div
          className="pointer-events-none absolute inset-x-0"
          style={{ top: art.top, y: storyY, opacity: storyOpacity }}
        >
          <motion.div
            ref={headingRef}
            className="mx-auto max-w-2xl px-6 pb-12"
            style={{ opacity: copyOpacity }}
          >
            <IntegratedHeading />
          </motion.div>
          <motion.div
            className="pointer-events-auto"
            style={{
              marginLeft: art.left,
              width: art.width,
              color: strokeColor,
            }}
          >
            <BuildingLineArt draw={draw} controlsOpacity={controlsOpacity} />
          </motion.div>
          <motion.div
            ref={paragraphRef}
            className="mx-auto max-w-6xl pl-[25.125rem] pr-6 pt-12"
            style={{ opacity: copyOpacity }}
          >
            <IntegratedParagraph />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

// A card inset from the edges that fills the viewport height above a minimum,
// with the photo cover-cropped inside it; the image* fields describe the photo's
// rendered box so the line art can follow the crop.
function photoFrame(
  viewportWidth: number,
  viewportHeight: number,
  rem: number,
) {
  const inset = CONTAINED_INSET_REM * rem;
  const width = viewportWidth - 2 * inset;
  // Never taller than the photo, so its sides and the line art stay inside the card
  const height = Math.max(
    Math.min(viewportHeight - 2 * inset, width / PHOTO_ASPECT),
    CONTAINED_MIN_HEIGHT_REM * rem,
  );
  const left = (viewportWidth - width) / 2;
  const top = inset;
  const imageWidth = Math.max(width, height * PHOTO_ASPECT);
  const imageHeight = imageWidth / PHOTO_ASPECT;
  // Crop from the top first so the villa's base stays inside the card
  const villaBaseTop =
    top + height - imageHeight * VILLA.bottom - VILLA_BASE_CLEARANCE_REM * rem;
  const imageTop = Math.min(
    top,
    Math.max(top + height - imageHeight, villaBaseTop),
  );
  return {
    left,
    top,
    width,
    height,
    imageLeft: left + (width - imageWidth) / 2,
    imageTop,
    imageWidth,
    imageHeight,
  };
}

function villaArt(photo: ReturnType<typeof photoFrame>) {
  const top = photo.imageTop + photo.imageHeight * VILLA.top;
  const width = photo.imageWidth * VILLA.width;
  const height = width / ART_ASPECT;
  return {
    top,
    left: photo.imageLeft + photo.imageWidth * VILLA.left,
    width,
    height,
    bottom: top + height,
  };
}
